/**
 * entryDiscipline — Stalker vs Chaser entry classification.
 *
 * The single most-cited theme across the educator research (Brooks
 * breakout-pullback, Shannon AVWAP retest, Raschke Turtle Soup, Grimes
 * snap pullback, Diaz pullback-to-9EMA): great trades are RETESTS, not
 * BREAKOUTS. This module classifies each entry from bar data alone:
 *
 *   - STALKER: entry came AFTER a 2-3 bar counter-direction retracement,
 *              OR is comfortably > 0.5 ATR away from the most recent
 *              swing extreme. The trader waited for it to come back.
 *
 *   - CHASER:  entry happened WITHOUT a recent pullback AND within
 *              0.3 ATR of a fresh swing extreme. Classic FOMO-shape
 *              entry into the move.
 *
 *   - NEUTRAL: in between — no clear signal either way.
 *
 * The classification is intentionally per-trade and direction-aware.
 * The discipline tag persists on `trade.barAnalysis.entryDiscipline` so
 * downstream analyzers can compute 30D WR by category and trade-card
 * chips can surface it inline.
 *
 * Pure function. No side effects.
 */

/**
 * Compute the Average True Range over the last N bars ending at `endIdx`.
 * Uses Wilder's smoothing for the final value (matches what every charting
 * platform shows). Returns null when not enough bars to seed.
 */
const computeATRAtIndex = (bars, endIdx, period = 14) => {
  if (!Array.isArray(bars) || endIdx < period) return null;
  // Seed = simple average of first `period` TRs ending at endIdx
  // Then Wilder-smooth from that index forward.
  const trs = [];
  for (let i = Math.max(1, endIdx - period * 2); i <= endIdx; i++) {
    const cur = bars[i];
    const prev = bars[i - 1];
    if (!cur || !prev) continue;
    const hi = Number(cur.high);
    const lo = Number(cur.low);
    const pc = Number(prev.close);
    if (![hi, lo, pc].every(Number.isFinite)) continue;
    trs.push(Math.max(hi - lo, Math.abs(hi - pc), Math.abs(lo - pc)));
  }
  if (trs.length < period) return null;
  // Simple seed average of the last `period` TRs — good enough for a
  // distance reference; Wilder's smoother would also work but adds
  // complexity for negligible accuracy gain in this context.
  let atr = 0;
  for (let i = trs.length - period; i < trs.length; i++) atr += trs[i];
  return atr / period;
};

/**
 * Locate the bar containing entryTs (open-time semantics: last bar with
 * time ≤ entryTs).
 */
const findEntryBarIdx = (bars, entryTs) => {
  if (!Array.isArray(bars) || bars.length === 0) return -1;
  let idx = -1;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= entryTs) idx = i;
    else break;
  }
  return idx;
};

/**
 * Did the last `lookbackBars` bars (just before entry) show a
 * counter-direction retracement? For longs that means at least one
 * dip lower-low immediately followed by a higher-low. For shorts
 * the mirror image. A retrace within the entry bar itself doesn't
 * count — too short to be a meaningful pullback.
 */
const hasRecentPullback = (bars, entryIdx, direction, lookbackBars = 3) => {
  if (entryIdx < lookbackBars) return false;
  // Inspect bars [entryIdx - lookbackBars .. entryIdx - 1] (exclude entry bar)
  const window = bars.slice(entryIdx - lookbackBars, entryIdx);
  if (window.length < 2) return false;
  for (let i = 1; i < window.length; i++) {
    const prev = window[i - 1];
    const cur = window[i];
    if (!prev || !cur) continue;
    if (direction === 'long') {
      // A "pullback bar" for a long: lower low than the prior bar
      if (Number(cur.low) < Number(prev.low)) return true;
    } else {
      // For a short: higher high than the prior bar
      if (Number(cur.high) > Number(prev.high)) return true;
    }
  }
  return false;
};

/**
 * Direction normalization (shared with trendContextScore / counterSignals).
 */
const normalizeDirection = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (s === 'long' || s === 'buy' || s === 'l') return 'long';
  if (s === 'short' || s === 'sell' || s === 's') return 'short';
  return null;
};

// Classification thresholds. Conservative defaults — easier to relax
// later than to walk back chip-chasing-paranoia complaints.
export const CHASER_MAX_DISTANCE_ATR = 0.3;
export const STALKER_MIN_DISTANCE_ATR = 0.5;
export const PULLBACK_LOOKBACK_BARS  = 3;
export const SWING_LOOKBACK_BARS     = 10;
export const ATR_PERIOD              = 14;

/**
 * Classify a trade's entry as stalker / chaser / neutral from bar data.
 *
 * @param {Object} params
 * @param {Array}  params.bars        Entry-timeframe bars (5m typical), sorted asc.
 * @param {number} params.entryTs     Unix seconds.
 * @param {number} params.entryPrice  Trade entry price.
 * @param {string} params.direction   'long' | 'short' | aliases.
 * @returns {Object|null}
 *   {
 *     discipline:           'stalker' | 'chaser' | 'neutral',
 *     reason:               human-readable one-liner,
 *     distanceFromExtreme:  number (ATR units; 0 = at the extreme),
 *     hadPullback:          boolean
 *   }
 *   Returns null when bars insufficient to evaluate.
 */
export const computeEntryDiscipline = ({ bars, entryTs, entryPrice, direction } = {}) => {
  const dir = normalizeDirection(direction);
  if (!dir) return null;
  if (!Number.isFinite(entryTs) || !Number.isFinite(entryPrice) || entryPrice <= 0) return null;
  if (!Array.isArray(bars) || bars.length === 0) return null;

  const entryIdx = findEntryBarIdx(bars, entryTs);
  // Need enough history to compute ATR + meaningful swing window.
  if (entryIdx < Math.max(ATR_PERIOD, SWING_LOOKBACK_BARS)) return null;

  const atr = computeATRAtIndex(bars, entryIdx, ATR_PERIOD);
  if (!Number.isFinite(atr) || atr <= 0) return null;

  // Recent swing extreme — within last SWING_LOOKBACK_BARS bars including
  // the entry bar. For LONG: the recent HIGH (resistance). For SHORT: the
  // recent LOW (support being broken from above).
  const startIdx = Math.max(0, entryIdx - SWING_LOOKBACK_BARS + 1);
  let extremePrice;
  if (dir === 'long') {
    extremePrice = -Infinity;
    for (let i = startIdx; i <= entryIdx; i++) {
      const h = Number(bars[i]?.high);
      if (Number.isFinite(h) && h > extremePrice) extremePrice = h;
    }
    if (extremePrice === -Infinity) return null;
  } else {
    extremePrice = Infinity;
    for (let i = startIdx; i <= entryIdx; i++) {
      const l = Number(bars[i]?.low);
      if (Number.isFinite(l) && l < extremePrice) extremePrice = l;
    }
    if (extremePrice === Infinity) return null;
  }

  // Distance from entry to extreme in ATR units. For a long, if entry is
  // BELOW the recent high (typical), distance is positive. For a short,
  // if entry is ABOVE the recent low, distance is positive.
  const distFromExtreme = dir === 'long'
    ? (extremePrice - entryPrice) / atr
    : (entryPrice - extremePrice) / atr;

  const pullback = hasRecentPullback(bars, entryIdx, dir, PULLBACK_LOOKBACK_BARS);

  // Classification:
  //   CHASER: very close to extreme AND no pullback in the lookback
  //   STALKER: comfortably past the extreme OR had a recent pullback
  //   NEUTRAL: anything in between
  let discipline;
  let reason;
  if (distFromExtreme < CHASER_MAX_DISTANCE_ATR && !pullback) {
    discipline = 'chaser';
    reason = `Entered within ${distFromExtreme.toFixed(2)}× ATR of the ${dir === 'long' ? 'recent high' : 'recent low'} with no pullback in the last ${PULLBACK_LOOKBACK_BARS} bars.`;
  } else if (distFromExtreme >= STALKER_MIN_DISTANCE_ATR || pullback) {
    discipline = 'stalker';
    const why = pullback
      ? `${PULLBACK_LOOKBACK_BARS}-bar pullback present before entry`
      : `Entered ${distFromExtreme.toFixed(2)}× ATR back from the ${dir === 'long' ? 'recent high' : 'recent low'}`;
    reason = `${why} — waited for it to come to you.`;
  } else {
    discipline = 'neutral';
    reason = `Mid-zone entry: ${distFromExtreme.toFixed(2)}× ATR from extreme, no clean pullback.`;
  }

  return {
    discipline,
    reason,
    distanceFromExtreme: Math.round(distFromExtreme * 100) / 100,
    hadPullback: pullback,
  };
};

// Exports for tests
export { computeATRAtIndex, hasRecentPullback, findEntryBarIdx, normalizeDirection };
